import type { FC, ReactNode, TouchEvent } from 'react'

import { useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useHomeState } from '@/hooks/use-home-state'
import { homeCommands } from '@/lib/cloud/store'

import { HOME_SCENES } from '@/constants/scenes'

/**
 * Small screens only: a scene slider under the stage. Arrows step through the four scenes,
 * the segments jump straight to one, and a sideways swipe on the strip moves one step.
 */
export const SceneSlider: FC = (): ReactNode => {
    const state = useHomeState()
    const startX = useRef<number | null>(null)
    const scene = HOME_SCENES[state.scene]

    const onTouchStart = (event: TouchEvent): void => {
        startX.current = event.touches[0]?.clientX ?? null
    }

    const onTouchEnd = (event: TouchEvent): void => {
        const start = startX.current
        const end = event.changedTouches[0]?.clientX

        startX.current = null

        if (start === null || end === undefined || Math.abs(end - start) < 40) return

        if (end < start) homeCommands.next()
        else homeCommands.prev()
    }

    return (
        <div
            className='intro-fade flex items-stretch border-b border-hairline desk:hidden'
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            <button
                type='button'
                onClick={() => homeCommands.prev()}
                aria-label='Previous scene'
                className='flex size-12 flex-none items-center justify-center border-r border-hairline text-ink'
            >
                <ChevronLeft className='size-5' strokeWidth={1.5} aria-hidden='true' />
            </button>

            <div className='flex min-w-0 flex-1 flex-col justify-center gap-2 px-4'>
                <p className='hud flex items-center gap-2 truncate text-ink' aria-live='polite'>
                    <span className='text-signal'>
                        {scene.number}
                    </span>
                    <span className='text-mute'>/ 04</span>
                    <span className='truncate'>
                        {scene.tab}
                    </span>
                </p>

                <div className='flex gap-1.5'>
                    {HOME_SCENES.map((item, index) => (
                        <button
                            key={item.number}
                            type='button'
                            onClick={() => homeCommands.goTo(index)}
                            aria-label={`Scene ${item.number}, ${item.tab}`}
                            aria-current={index === state.scene ? 'true' : undefined}
                            className='flex h-4 flex-1 items-center'
                        >
                            <span className={cn('block h-px w-full transition-colors duration-300', index === state.scene ? 'bg-signal' : 'bg-hairline')} />
                        </button>
                    ))}
                </div>
            </div>

            <button
                type='button'
                onClick={() => homeCommands.next()}
                aria-label='Next scene'
                className='flex size-12 flex-none items-center justify-center border-l border-hairline text-ink'
            >
                <ChevronRight className='size-5' strokeWidth={1.5} aria-hidden='true' />
            </button>
        </div>
    )
}
